import { useCallback, useEffect, useRef, useState } from "react";
import { parsePlansList, type Plan } from "@jackedlogs/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";

// Mirrors apps/mobile's useAsyncResource status union without coupling to
// the mobile hook — hosts map their resource status onto this. (Lived in
// PlansView until HR-S4 retired that surface for the hub's Library.)
export type PlansResourceStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

// Shared plans-list data layer for the web-rnw console. Mobile's PlanScreen
// keeps its own useAsyncResource (AsyncStorage cache + focus/AppState
// re-kick) for now; both go through the shared parsePlansList, so the
// parse/sort is single-source. Convergence onto this hook is tracked in
// next/anti-monster.md. apiFetch via a ref so a fresh literal per render
// never re-kicks the fetch (the unstable-object-dep loop — see
// useAsyncResource / usePrograms).
export type UsePlansOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Bump to re-kick the fetch (e.g. after a mutation elsewhere). */
  refreshSignal?: number;
};

export function usePlans(opts: UsePlansOptions) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [status, setStatus] = useState<PlansResourceStatus>("idle");

  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
  });

  const refresh = useCallback(async (): Promise<void> => {
    setStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    try {
      const raw = await apiRef.current<unknown>("/api/me/plans");
      setPlans(parsePlansList(raw));
      setStatus("loaded");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, opts.refreshSignal]);

  // Mirrors usePrograms.deleteProgram: re-fetch even on outcome (the server
  // may have partially moved) and hand the error back instead of rendering.
  // A queued delete returns null — for the athlete it succeeded, and on
  // mobile it is durable (DX-2/F).
  const deletePlan = useCallback(
    async (id: string): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "DELETE",
        path: `/api/me/plans/${encodeURIComponent(id)}`,
      });
      void refresh();
      return writeError(result);
    },
    [refresh],
  );

  return { plans, status, refresh, deletePlan };
}
